import { vec2 } from "@tlaukkan/tsm";
import { Entity, Not, System } from "ecsy";
import * as particles from 'pixi-particles';
import * as PIXI from 'pixi.js';
import { SpriteTrackingParticles, AimForDirection, Antibody, AoE, BloodCell, Buffed, Bullet, Consumable, ConsumableType, Cytokine, Dead, ElementInfo, Healed, OnScreen, Ownership, Physics, Renderable, TaggedVirus, TargetAcquiredThisFrame, Transform, VisibleRange, ReturningHome, BuffPreview, PreviewOnly, ElectricConductor, ElectricCharge, Virus, ReplaceSelf, HasSplit, InfectionGrowth, Spawner, VirusLoadIncrease } from "../../../shared/components";
import { Background, BehaviorsConstants, RenderEffects, Resolution } from "../../../shared/constants";
import { ElementStats, StatName } from "../../../shared/elementDisplayInfo";
import { getEntityById, getId } from "../../../shared/entityFactory";
import { GameElement } from "../../../shared/gameElementTypes";
import { wait } from "../../../shared/systems/animationSystem";
import { addComponent, angleFromUp, metersToPixels, worldToScreenPosition } from "../../../shared/utils";
import { GameClient } from "../gameClient";
import { Colors } from "../palettes/colorPalette";
import { worldToTrailSpace } from "../rendering/backgroundFluid";
import { ParticleFactory } from "../rendering/particleFactory";
import { RenderEngine } from "../rendering/renderEngine";

export class ParticleSystem extends System {
	pixiApp: PIXI.Application;
	particleContainer: particles.LinkedListContainer;
	trailParticleContainer: particles.LinkedListContainer;
	explosions: Array<particles.Emitter>;
	renderer!: RenderEngine;
	game: GameClient;
	bulletsFired = new Array<Entity>();
	particles: ParticleFactory;
	genericBuffColor: string;
	healthBuffColor: string;
	harmWaveColor: string;

	constructor(world: any, attributes: any) {
		// @ts-ignore
		super(world, attributes);
		this.game = attributes.game;
		this.renderer = this.game.renderEngine;
		this.pixiApp = this.renderer.pixiApp;
		this.explosions = [];
		this.particleContainer = this.renderer.particleContainer;
		this.trailParticleContainer = this.renderer.trailParticleContainer;
		this.particles = this.renderer.particleFactory;
		this.genericBuffColor = "ffffff";
		this.healthBuffColor = PIXI.utils.hex2string(Colors.AlternateMembraneColor);
		this.harmWaveColor = PIXI.utils.hex2string(Colors.VirusA);
	}

	init() {
	}

	execute(delta: number, time: number) {
		this.particleContainer.position.copyFrom(this.renderer.sceneRoot.position);
		this.particleContainer.scale.copyFrom(this.renderer.sceneRoot.scale);

		this.queries.wave.added?.forEach(entity => {
			const transform = entity.getComponent(Transform);
			const aoe = entity.getComponent(AoE);
			const info = entity.getComponent(ElementInfo);

			const position = worldToScreenPosition(transform.position);
			const maxRadius = aoe.currentRadius + aoe.radiusPerSecond * aoe.durationRemaining;

			const startRadius = metersToPixels(aoe.currentRadius);
			const endRadius = metersToPixels(maxRadius);

			//Do particle effects
			const effects = new Array<particles.Emitter>();
			let waterWave: particles.Emitter | null = null;
			switch (info.type) {
				case GameElement.EXPLOSION:
					effects.push(this.particles.Wavefront(startRadius, endRadius, aoe.durationRemaining, '000000', 'ff0000'));
					effects.push(this.particles.Explosion(startRadius, endRadius, aoe.durationRemaining));
					effects.push(this.particles.Explosion(startRadius, endRadius, aoe.durationRemaining, 'ffffff', 'ffffff'));
					effects.push(this.particles.GrowingBall(startRadius, endRadius * 4, aoe.durationRemaining / 2, 1.0, 1.0))
					waterWave = this.particles.Wavefront(startRadius, endRadius * 1.2, aoe.durationRemaining * 1.2, "ffffff", "ffffff", 1.0, 1.0, 1, this.trailParticleContainer);
					break;
				case GameElement.FORCE_EXPLOSION:
					effects.push(this.particles.Wavefront(startRadius, endRadius, aoe.durationRemaining, undefined, undefined, undefined, undefined, 0.2));
					waterWave = this.particles.Wavefront(startRadius, endRadius, aoe.durationRemaining, "ffffff", "ffffff", 1.0, 1.0, 1, this.trailParticleContainer);
					break;
				case GameElement.HEAL_WAVE:
					effects.push(this.particles.Wavefront(startRadius, endRadius, aoe.durationRemaining, '000000', this.genericBuffColor));
					break;
				case GameElement.HARM_WAVE:
					effects.push(this.particles.Wavefront(startRadius, endRadius, aoe.durationRemaining, '000000', this.harmWaveColor));
					break;
			}
			for (const effect of effects) {
				effect.updateOwnerPos(position.x, position.y);
				effect.playOnceAndDestroy();
			}

			//Do trail effects
			if (waterWave !== null) {
				const trailPosition = worldToTrailSpace(transform.position.x, transform.position.y);
				waterWave.updateOwnerPos(trailPosition.x, trailPosition.y);
				waterWave.playOnceAndDestroy();
			}
		});

		this.queries.dead.added?.forEach(entity => {
			if (!entity.hasComponent(Transform)) return; //seen with a lot of splitters going off.
			const owned = entity.hasComponent(Ownership) && entity.getComponent(Ownership).playerId === this.game.playerID
			//Only our own blood and immune cells get the blood effects
			if (owned && (entity.hasComponent(BloodCell) || entity.hasComponent(Antibody))) {
				const transform = entity.getComponent(Transform);
				const position = worldToScreenPosition(transform.position);
				if (entity.hasComponent(BloodCell)) {
					const scaleInvert = 1 / this.particleContainer.scale.x;
					const emitter = this.particles.BloodSplatter(2.0 * scaleInvert, 0.3, 0.0, 0.5);
					emitter.updateOwnerPos(position.x, position.y);
					emitter.playOnceAndDestroy();
				}
				else if (entity.hasComponent(Antibody)) {
					{
						const transform = entity.getComponent(Transform);
						const position = worldToScreenPosition(transform.position);
						const emitter = this.particles.GrowingBall(1, 300, 0.15, 0.5, 0.5, "#ffffff", "#ff0000");
						emitter.updateOwnerPos(position.x, position.y);
						emitter.playOnceAndDestroy();
					}
					// wait(RenderEffects.DeathScaleTime).then(() => {
					// 	const emitter = this.particles.BloodSplatter(0.2, 0.5, 0.1, 1.0);
					// 	emitter.updateOwnerPos(position.x, position.y);
					// 	emitter.playOnceAndDestroy();
					// });
				}

			} else {
				{
					const transform = entity.getComponent(Transform);
					const position = worldToScreenPosition(transform.position);
					const emitter = this.particles.CellBurst(entity);
					emitter.updateOwnerPos(position.x, position.y);
					emitter.playOnceAndDestroy();
				}
				{ //in trails
					const transform = entity.getComponent(Transform);
					const position = worldToTrailSpace(transform.position.x, transform.position.y);
					const particle = entity.hasComponent(Virus) ? "particle_blue" : "particle";
					const emitter = this.particles.CellBurst(entity, 0.8, particle, this.trailParticleContainer);
					emitter.updateOwnerPos(position.x, position.y);
					emitter.playOnceAndDestroy();
				}
			}

			if (entity.hasComponent(Virus)) {
				const transform = entity.getComponent(Transform);
				const position = worldToScreenPosition(transform.position);
				const emitter = this.particles.VirusDeathIndicator(0.1, 1.5, 0.2, 0.6, 0.1);
				emitter.updateOwnerPos(position.x, position.y);
				emitter.playOnceAndDestroy();
			}
		});

		this.queries.healed.added?.forEach(entity => {
			const transform = entity.getComponent(Transform);
			const position = worldToScreenPosition(transform.position);
			const healthEmitter = this.particles.HealBurst(entity);
			healthEmitter.updateOwnerPos(position.x, position.y);
			entity.removeComponent(Healed);
			healthEmitter.playOnceAndDestroy();
		});


		this.bulletsFired.forEach(entity => {
			const transform = entity.getComponent(Transform);
			const movement = entity.getComponent(AimForDirection);
			if (!movement || !movement.fixedDirection) return;
			const direction = movement.fixedDirection.copy().negate();
			const position = worldToScreenPosition(transform.position);
			const degrees = angleFromUp(direction) * -PIXI.RAD_TO_DEG + 90;

			const bubbleEmitter = this.particles.Bubbles(5);
			bubbleEmitter.updateOwnerPos(position.x, position.y);
			bubbleEmitter.maxStartRotation = (degrees - 45) + 180;
			bubbleEmitter.minStartRotation = (degrees + 45) + 180;
			bubbleEmitter.playOnceAndDestroy();
		});
		if (this.queries.bullet.added) {
			this.bulletsFired = this.queries.bullet.added;
		}

		this.queries.protein.added?.forEach(entity => {
			const transform = entity.getComponent(Transform);
			const position = worldToScreenPosition(transform.position);
			const consumable = entity.getComponent(Consumable);
			const type = consumable.type;
			if (type !== ConsumableType.Interferon) return;
			const emitter = this.particles.TagVirus(30, 100, 1);
			emitter.updateOwnerPos(position.x, position.y);
			emitter.playOnceAndDestroy();
		});

		//Target lock
		for (const entity of this.queries.targetAcquires.results) {
			const tracker = this.getTrackingParticleContainer(entity);
			const emitter = this.particles.Alert(tracker.container);
			tracker.emitters.push(emitter);

			const transform = entity.getComponent(Transform);
			emitter.updateOwnerPos(0, -metersToPixels(transform.size.y));
			emitter.playOnceAndDestroy();
		}

		//Return home for seekers
		for (const entity of this.queries.returnHome.added || []) {
			const tracker = this.getTrackingParticleContainer(entity);
			const emitter = this.particles.Question(tracker.container);
			tracker.emitters.push(emitter);

			const transform = entity.getComponent(Transform);
			emitter.updateOwnerPos(0, -metersToPixels(transform.size.y));
			emitter.playOnceAndDestroy();
		}

		this.queries.buffed.changed?.forEach(entity => {
			if (getId(entity) === null) return; //guard against death
			const buffed = entity.getComponent(Buffed);

			//Play buff source effect if there is a souce
			for (const source of buffed.lastBuffSources) {
				const target = getEntityById(source);
				if (target && target.hasComponent(Transform)) {
					const tracker = this.getTrackingParticleContainer(target);
					this.createBuffEffect(tracker, buffed.lastBuffed, true);
				}
			}

			if (buffed.lastBuffed === "charge") {
				return; //too noisy
			}

			//Play buff effect
			const hadSource = buffed.lastBuffSources.length > 0;
			const tracker = this.getTrackingParticleContainer(entity);
			this.createBuffEffect(tracker, buffed.lastBuffed, false, hadSource ? 0.3 : 0);
		});

		//Buff previews
		this.queries.buffPreviews.added?.forEach(entity => {
			const preview = entity.getComponent(BuffPreview);
			const color = preview.stat === 'health' ? this.healthBuffColor : this.genericBuffColor;
			const tracker = this.getTrackingParticleContainer(entity);
			const emitter = preview.isOutgoing
				? this.particles.Wavefront(5, 60, 0.4, '444444', color, 0.3, 0.3, 0.4, tracker.container)
				: this.particles.WaveCollapse(60, 5, 0.4, 'dddddd', color, 0.1, 0.1, 0.4, tracker.container);
			emitter.emitterLifetime = -1;
			emitter.updateOwnerPos(0, 0);
			emitter.playOnceAndDestroy();
			tracker.emitters.push(emitter);
			preview.activeEmitter = emitter;
		});

		this.queries.buffPreviews.removed?.forEach(entity => {
			const preview = entity.getComponent(BuffPreview) || entity.getRemovedComponent(BuffPreview);
			if (preview.activeEmitter) {
				preview.activeEmitter.destroy();
			}
		});

		//Tagged marker
		this.queries.tagged.added?.forEach(entity => {
			const emitter = this.particles.Wavefront(5, 35, 0.4, 'ffaaaa', '999999', 0.8, 0.7, 0.2);
			emitter.emitterLifetime = BehaviorsConstants.TagProteinSuspendTime - 0.4;
			const transform = entity.getComponent(Transform);
			const position = worldToScreenPosition(transform.position);
			emitter.updateOwnerPos(position.x, position.y);
			emitter.playOnceAndDestroy();
		});

		//Tracker managing code. Trackers update positions of emitters that are attached to entities
		this.queries.ongoingEmitters.added?.forEach(entity => {
			const activeEmitter = entity.getComponent(SpriteTrackingParticles);
			this.renderer.inSceneUI.addChild(activeEmitter.container);
		});

		this.queries.ongoingEmitters.removed?.forEach(entity => {
			const tracker = entity.getComponent(SpriteTrackingParticles) || entity.getRemovedComponent(SpriteTrackingParticles);
			for (const emitter of tracker.emitters) {
				if (emitter.ownerPos !== null) emitter.destroy();
			}
			tracker.container.destroy();
		});

		const removeOnGoingEmitters: Entity[] = [];
		for (const entity of this.queries.ongoingEmitters.results) {
			const tracker = entity.getComponent(SpriteTrackingParticles);

			//Remove emitters that are dead.
			tracker.emitters = tracker.emitters.filter(emitter => emitter.ownerPos !== null);

			//Get rid of unused trackers or they will just accumulate all over
			if (tracker.emitters.length === 0) {
				removeOnGoingEmitters.push(entity);
				continue;
			};

			//Track
			const renderable = entity.getComponent(Renderable);
			const sprite = renderable.sprite;
			tracker.container.position.copyFrom(sprite.position);
		}
		removeOnGoingEmitters.forEach(entity => entity.removeComponent(SpriteTrackingParticles));

		for (const entity of this.queries.electric.results) {
			//Draw a towards each other entity in the conductor components conductors list
			const electric = entity.getComponent(ElectricConductor);

			const wired = entity.hasComponent(ElectricCharge) || electric.incoming.size > 0 || electric.outgoing.size > 0 || electric.liveCurrent.size > 0;
			if (wired && Math.random() < 0.05) {
				const emitter = this.particles.Electricity();
				const transform = entity.getComponent(Transform);
				const position = worldToScreenPosition(transform.position);
				emitter.updateOwnerPos(position.x, position.y);
				emitter.playOnceAndDestroy();
			}
		}

		for (const entity of this.queries.infectionGrowth.results) {
			const transform = entity.getComponent(Transform); //not in query so we can use changed event..
			if (!transform) return;
			const emitter = this.particles.WaveCollapse(60, 5, 0.4, 'dddddd', 'dddddd', 0.1, 0.1, 0.4);
			const position = worldToScreenPosition(transform.position);
			emitter.updateOwnerPos(position.x, position.y);
			emitter.playOnceAndDestroy();
		};
	}

	createBuffEffect(tracker: SpriteTrackingParticles, stat: keyof ElementStats | 'none', isBuffSource = false, delay = 0) {
		const color = stat === 'health' ? this.healthBuffColor : this.genericBuffColor;
		const emitter = isBuffSource
			? this.particles.Wavefront(5, 60, 0.4, '000000', color, 0.8, 0.7, 0.5, tracker.container)
			: this.particles.WaveCollapse(60, 5, 0.4, '000000', color, 0.8, 0.7, 0.5, tracker.container);
		emitter.updateOwnerPos(0, 0);
		tracker.emitters.push(emitter);

		//Small delay is useful for buffs to line up withe a source effect
		if (delay > 0) {
			setTimeout(() => {
				if ((emitter as any).ownerPos !== null) {
					emitter.playOnceAndDestroy();
				}
			}, delay * 1000);
		}
		else {
			emitter.playOnceAndDestroy();
		}
		return emitter;
	}

	createUIEffect(position: vec2) {
		const coinsEmitter = this.particles.SellCoins();
		const screenPos = worldToScreenPosition(position);
		coinsEmitter.updateOwnerPos(screenPos.x, screenPos.y);
		coinsEmitter.playOnceAndDestroy();
	}

	createWaveEffect(entity: Entity, startRadius: number, endRadius: number, duration: number) {
		const info = entity.getComponent(ElementInfo);
		const effects: particles.Emitter[] = [];
		switch (info.type) {
			case GameElement.EXPLOSION:
				break;
			case GameElement.FORCE_EXPLOSION:
				effects.push(this.particles.Wavefront(startRadius, endRadius, duration));
				break;
			case GameElement.HEAL_WAVE:
				effects.push(this.particles.Wavefront(startRadius, endRadius, duration, '000000', this.genericBuffColor));
				break;
		}
		return effects;
	}

	getTrackingParticleContainer(entity: Entity) {
		if (entity.hasComponent(SpriteTrackingParticles)) {
			return entity.getComponent(SpriteTrackingParticles);
		}
		else {
			const tracker = new PIXI.Container();
			return addComponent(entity, SpriteTrackingParticles, { container: tracker });
		}
	}
}

ParticleSystem.queries = {
	wave: { components: [AoE, Transform, OnScreen], listen: { added: true } },
	healed: { components: [Healed, OnScreen], listen: { added: true } },
	dead: { components: [Dead, Transform, OnScreen, Not(Cytokine), Not(HasSplit)], listen: { added: true } },
	bullet: { components: [Bullet, Physics, Transform, AimForDirection, OnScreen], listen: { added: true } },
	protein: { components: [Consumable, Not(Bullet), Transform, OnScreen], listen: { added: true } },
	targetAcquires: { components: [TargetAcquiredThisFrame, VisibleRange, Antibody] },
	returnHome: { components: [ReturningHome, VisibleRange, Antibody], listen: { added: true } },
	buffed: { components: [Buffed, Antibody], listen: { added: true, changed: true } },
	tagged: { components: [Renderable, Transform, TaggedVirus], listen: { added: true } },
	buffPreviews: { components: [Renderable, BuffPreview, Transform], listen: { added: true, removed: true } },
	electric: { components: [Renderable, Transform, ElectricConductor] },
	infectionGrowth: {
		components: [InfectionGrowth, VirusLoadIncrease]
	},

	ongoingEmitters: { components: [SpriteTrackingParticles, Renderable], listen: { added: true, removed: true } }
}
